import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { AppError } from 'src/app/core/models/api-error';
import { selectHasPermission } from 'src/app/core/store/permissions/permissions.selectors';
import { ErrorMessageService } from 'src/app/core/services/error-message.service';

import { MfaPolicySettings } from '../../../models/mfa.models';
import { MfaService } from '../../../services/mfa.service';
import {
  MfaPolicyDialogComponent,
  MfaPolicyDialogData,
} from '../mfa-policy-dialog/mfa-policy-dialog.component';

/**
 * Tenant-wide MFA policy: what is configured, and a way in to change it.
 *
 * Two permissions, matching the endpoint: the parent gates this on
 * `access:settings_security`, what the GET checks, and editing asks for
 * `edit:settings_security`. The form itself lives in
 * `MfaPolicyDialogComponent` -- at rest this is a summary, so nothing on the
 * page looks editable when it is not, and nothing holds an unsaved value.
 *
 * Every class it renders is defined in its own stylesheet: view encapsulation
 * keeps the parent page's `.security__*` rules out of this template.
 */
@Component({
  selector: 'app-mfa-policy-panel',
  templateUrl: './mfa-policy-panel.component.html',
  styleUrls: ['./mfa-policy-panel.component.scss'],
})
export class MfaPolicyPanelComponent implements OnInit, OnDestroy {
  settings: MfaPolicySettings | null = null;
  error: AppError | null = null;
  loading = false;

  /** Whether this user may write the policy, not merely read it. */
  canEdit$: Observable<boolean> = this.store.select(
    selectHasPermission('edit:settings_security')
  );

  /** Coverage readings the user has waved away, by the numbers they showed.
   *  Held in memory, not `localStorage`: a card that says nine people are
   *  about to lose access should come back next session. */
  private dismissed = new Set<string>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly mfa: MfaService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly errorMessages: ErrorMessageService,
    private readonly store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- reading -------------------------------------------------------------

  load(): void {
    this.error = null;
    this.loading = true;
    this.mfa
      .policySettings()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => this.apply(response.object ?? null),
        error: (error) => (this.error = this.errorMessages.parse(error as never)),
      });
  }

  private apply(settings: MfaPolicySettings | null): void {
    this.settings = settings;
  }

  get coverageTotal(): number {
    return this.settings?.coverage?.total ?? 0;
  }

  get coverageEnrolled(): number {
    return this.settings?.coverage?.enrolled ?? 0;
  }

  /** Users with no factor, who lose access once grace lapses under `required`. */
  get coverageGap(): number {
    return Math.max(0, this.coverageTotal - this.coverageEnrolled);
  }

  get requiredSince(): string | null {
    return this.settings?.required_since ?? null;
  }

  // --- the summary ---------------------------------------------------------

  get policyLabel(): string {
    return this.settings
      ? this.translate.instant(
          `settings_security.policy.value.${this.settings.policy}`
        )
      : '';
  }

  get emailOtpLabel(): string {
    return this.translate.instant(
      this.settings?.email_otp_enabled
        ? 'settings_security.policy.email_otp_on'
        : 'settings_security.policy.email_otp_off'
    );
  }

  // --- coverage cards ------------------------------------------------------

  /** Keyed on the numbers, so the card returns when the counts move. */
  private cardKey(card: string): string {
    return `${card}:${this.coverageEnrolled}/${this.coverageTotal}`;
  }

  showsCard(card: 'coverage' | 'gap'): boolean {
    if (!this.settings || this.dismissed.has(this.cardKey(card))) {
      return false;
    }
    return card === 'coverage' || this.coverageGap > 0;
  }

  dismissCard(card: 'coverage' | 'gap'): void {
    this.dismissed.add(this.cardKey(card));
  }

  // --- editing -------------------------------------------------------------

  /** Opens the form. The dialog owns the save, and hands back what it wrote. */
  openEditor(): void {
    const settings = this.settings;
    if (!settings) {
      return;
    }

    this.canEdit$.pipe(take(1), takeUntil(this.destroy$)).subscribe((canEdit) => {
      const data: MfaPolicyDialogData = {
        settings,
        coverageGap: this.coverageGap,
        canEdit,
      };

      this.dialog
        .open(MfaPolicyDialogComponent, { data })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((saved: MfaPolicySettings | undefined) => {
          // Closing discards whatever the form held; only a save changes what
          // the summary reads.
          if (saved) {
            this.apply(saved);
          }
        });
    });
  }
}
